import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import ReactDOM from "react-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { FaTrash } from "react-icons/fa";
import { hasPermission } from "../../utils/permission";
import { MASTER_API } from "../../apiConfig"; // adjust path if needed
import { authHeaders } from "../../utils/authHeaders";

// Every master-service route now sits behind JWT/RBAC middleware, so plain axios
// calls come back 403. This instance attaches authHeaders() to every request.
const api = axios.create();
api.interceptors.request.use((cfg) => {
  const h = authHeaders() || {};
  const isForm = typeof FormData !== "undefined" && cfg.data instanceof FormData;
  Object.entries(h).forEach(([k, v]) => {
    if (isForm && k.toLowerCase() === "content-type") return; // let browser set multipart boundary
    if (cfg.headers && typeof cfg.headers.set === "function") cfg.headers.set(k, v);
    else cfg.headers = { ...(cfg.headers || {}), [k]: v };
  });
  return cfg;
});

// ==========================================
// STYLING ALIGNED WITH WORKCENTER & BOM
// ==========================================
const smallInput = { width: "100%", fontSize: "12px", padding: "4px 6px", height: "28px", boxSizing: "border-box", border: "1px solid #ccc", borderRadius: "4px" };
const smallLabel = { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: 6, color: "#334155" };
const thStyle = { padding: 8, borderBottom: "1px solid #ccc", textAlign: "left", whiteSpace: "nowrap", color: "#334155" };
const tdStyle = { padding: 8, borderBottom: "1px solid #eee", whiteSpace: "nowrap" };

const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "28px",
    height: "28px",
    fontSize: "12px",
    borderColor: "#ccc",
    boxShadow: "none",
    '&:hover': { borderColor: "#b3b3b3" }
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
    display: "flex",
    alignItems: "center"
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#334155"
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "26px"
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "2px 6px"
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "2px"
  }),
  menu: (base) => ({
    ...base,
    fontSize: "12px",
    zIndex: 99999
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "12px",
    padding: "8px 12px"
  })
};

const emptyComponent = () => ({
  item: "", process_id: "", sub_process_id: "", item_category: "", component_product: "", description: "", quantity: "", uom: "", assembly: "", valid_from: "", valid_to: "",
});

const emptyOperation = () => ({
  process_id: "", process_name: "",
  sub_process_id: "", sub_process_name: "",
  workcenterName: "", workcenter_description: "",
  activities: { activity_uom: "", setup_value: "", machine_value: "", labour_value: "", activity_1_value: "", activity_2_value: "", activity_3_value: "" },
  processParams: {},
  machine_parameters: Array.from({ length: 10 }, () => ({ name: "", value: "", uom: "" })),
  inspection_parameters: []
});

// Shared check used both to decide which BOM children are Semi-Finished
// components (SFGs) and to validate that all of them have been routed.
const isSfgComponent = (c) =>
  String(c.component_product).startsWith("8") ||
  (c.product_type && c.product_type.toLowerCase().includes("semi")) ||
  (c.product_type && c.product_type.toUpperCase() === "HALB");

// The /routings/productdetails API doesn't guarantee camelCase field names
// (e.g. it may send "producttype" or "product_type" instead of "productType").
// This normalizes every record to the canonical camelCase keys the cascading
// filters expect, so "Product Type" (and the other attribute filters) always
// find their options instead of silently showing "No options".
const normalizeProductRecord = (raw) => {
  const lookup = {};
  Object.keys(raw || {}).forEach((k) => {
    lookup[k.toLowerCase().replace(/[_\s]/g, "")] = raw[k];
  });
  const pick = (...aliases) => {
    for (const alias of aliases) {
      const val = lookup[alias.toLowerCase().replace(/[_\s]/g, "")];
      if (val !== undefined && val !== null && val !== "") return val;
    }
    return "";
  };
  return {
    ...raw,
    productCode: pick("productCode", "productcode", "product_code"),
    productDescription: pick("productDescription", "productdescription", "product_description"),
    productType: pick("productType", "producttype", "product_type"),
    construction: pick("construction"),
    code: pick("code"),
    cableType: pick("cableType", "cabletype", "cable_type"),
    category: pick("category"),
    noOfCores: pick("noOfCores", "noofcores", "no_of_cores", "cores"),
    color: pick("color"),
    nominalCrossSection: pick("nominalCrossSection", "nominalcrosssection", "crossSection", "cross_section"),
    referenceOfStandard: pick("referenceOfStandard", "referenceofstandard", "stdRef", "std_ref"),
    voltage: pick("voltage"),
    uom: pick("uom"),
  };
};

const Routings = () => {
  const canView = hasPermission("MD_ROUTING", "view");
  const canCreate = hasPermission("MD_ROUTING", "create");
  const canEdit = hasPermission("MD_ROUTING", "edit");
  const canApprove = hasPermission("MD_ROUTING", "approve");
  const canDelete = hasPermission("MD_ROUTING", "delete");
  const [routingList, setRoutingList] = useState([]);
  const [products, setProducts] = useState([]);
  const [workcenters, setWorkcenters] = useState([]);
  const [plants, setPlants] = useState([]);
  const [routingHeaders, setRoutingHeaders] = useState([]);

  //eslint-disable-next-line no-unused-vars
  const [routingConfigs, setRoutingConfigs] = useState([]);
  const [workcenterConfigs, setWorkcenterConfigs] = useState([]);


  const [showModal, setShowModal] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formHeader, setFormHeader] = useState({ productCode: "", mainLocation: "", startDate: "", endDate: "", alternative_bom: "", baseUnit: "", uom: "", reference_routing: "", product_type: "" });
  const [operations, setOperations] = useState([emptyOperation()]);
  const [components, setComponents] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [activeTab, setActiveTab] = useState("components");
  const [routingLogs, setRoutingLogs] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bomAlternatives, setBomAlternatives] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [componentsReadOnly, setComponentsReadOnly] = useState(false);

  const [draftRoutings, setDraftRoutings] = useState([]);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDraftView, setIsDraftView] = useState(false);
  const [isPublishingAll, setIsPublishingAll] = useState(false);

  // Routings that exist in routings_data but whose productCode has no
  // matching row in productdetails — these are the ones that silently
  // disappear from the per-productType counts (they get INNER JOINed out).
  const [orphanRoutings, setOrphanRoutings] = useState([]);
  const [showOrphanModal, setShowOrphanModal] = useState(false);
  const [loadingOrphans, setLoadingOrphans] = useState(false);

  const checkOrphanRoutings = async () => {
    setLoadingOrphans(true);
    try {
      const res = await api.get(`${MASTER_API}/routings/orphans`);
      setOrphanRoutings(res.data?.data || []);
      setShowOrphanModal(true);
    } catch (err) {
      console.error("[checkOrphanRoutings] error:", err);
      toast.error("Failed to check for missing product master records.");
    } finally {
      setLoadingOrphans(false);
    }
  };

  //  PAGINATION STATES
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [selectedRfqFilter] = useState(null);

  // Backend now returns only `itemsPerPage` routings per request (instead
  // of all 12k+ at once) plus the true total count, so the existing
  // Previous/Next controls below fetch a fresh page from the server
  // instead of slicing an already-fully-loaded array in the browser.
  const [listTotal, setListTotal] = useState(0);
  // Holds the productCode list derived from the currently-active attribute filters
  // (Product Type, Construction, ...), kept in sync via the effect below. A ref
  // (rather than reading `filteredProducts` directly) is needed so the
  // [currentPage]-triggered loadRoutingList call always sees the latest filter
  // state without having to add it as a dependency of that effect.
  const activeProductCodesRef = useRef([]);
  const activeProductTypeRef = useRef("");
  const activeHasCodeFilterRef = useRef(false);

  // ── Cascading attribute filters, same shape/behavior as the BOM listing page ──
  const [filters, setFilters] = useState({
    productType: null,
    construction: null,
    code: null,
    cableType: null,
    category: null,
    noOfCores: null,
    color: null,
    nominalCrossSection: null,
    referenceOfStandard: null,
    voltage: null,
    productCode: null,
    productDescription: null
  });

  // ── Routing Tree modal state (same pattern as BOM's tree modal) ──
  const [expandedNodes, setExpandedNodes] = useState({});
  const [showTreeModal, setShowTreeModal] = useState(false);
  const [routingTree, setRoutingTree] = useState(null);

  const loadedBomRef = useRef({ productCode: null, altBom: null });
  
  const productRoutingIdsRef = useRef({});

  // true when the View/Edit modal was opened by clicking a product code
  // inside the Routing Tree modal — used so Close returns to the Tree
  // instead of dropping the user back on the main listing screen.
  const openedFromTreeRef = useRef(false);

  // true  = user clicked "+ Create Routing" → Save must save ALL levels in routingCache
  // false = user clicked "Edit" on an existing routing → Save only saves the current level
  const isCreateModeRef = useRef(false);

  const [bomLevelStack, setBomLevelStack] = useState([]);
  const [currentBomChildren, setCurrentBomChildren] = useState([]); 

  const visitedSfgRef = useRef({});
  const [sfgChoices, setSfgChoices] = useState([]);

  // Tracks, for every BOM level (product code) that has been loaded, which of
  // its direct components are Semi-Finished (SFG) and therefore need their own
  // routing. Populated inside loadBomLevel for every level visited, so Save can
  // walk the WHOLE BOM tree (not just the root's direct children) to confirm
  // every nested SFG has actually been routed before a multi-level routing is
  // allowed to be saved.
  const sfgMapRef = useRef({});

  const [routingCache, setRoutingCache] = useState({});

  // We need a ref to track editingId inside async callbacks without stale closure
  const editingIdRef = useRef(null);

  useEffect(() => {
    editingIdRef.current = editingId;
  }, [editingId]);

  // FIX: formHeaderRef — handleProcessIdSelect / handleSubProcessIdSelect read
  // product_type to decide whether to fetch Inspection Parameters. Without this
  // ref they close over a stale formHeader (captured at the time the handler
  // was created), so right after a routing loads — before formHeader's
  // setState has flushed — re-picking a process (e.g. switching away from
  // "Insulation" and back) sends product_type="" to the API. The backend then
  // returns [] for anything that isn't "semi finished", which wipes out the
  // existing Inspection Parameters for that operation. Reading from this ref
  // instead always uses the latest product_type.
  const formHeaderRef = useRef(formHeader);

  useEffect(() => {
    formHeaderRef.current = formHeader;
  }, [formHeader]);

  useEffect(() => {

    const fetchSafe = async (url, setter, normalize) => {
      try {
        const res = await api.get(url);
        let data;
        if (Array.isArray(res.data)) {
          data = res.data;
        } else if (Array.isArray(res.data?.data)) {
          data = res.data.data;
        } else {
          data = [];
        }
        setter(normalize ? data.map(normalize) : data);
      } catch (err) {
        console.error(`Failed to load data from ${url}`, err);
        setter([]);
      }
    };

    // Use the same product-listing endpoint BOM uses (MASTER_API + /bom/products),
    // since /api/routings/productdetails only ever returns productCode/productDescription/uom
    // and is missing productType, construction, code, cableType, category, noOfCores,
    // color, nominalCrossSection, referenceOfStandard, and voltage — which is why every
    // attribute dropdown on this page was showing "No options".
    fetchSafe(`${MASTER_API}/bom/products`, setProducts, normalizeProductRecord);
    fetchSafe(`${MASTER_API}/workcenter`, setWorkcenters);
    fetchSafe(`${MASTER_API}/configuration/location`, setPlants);
    fetchSafe(`${MASTER_API}/routings/alternative-bom`, setBomAlternatives);
    fetchSafe(`${MASTER_API}/routing-configuration`, setRoutingConfigs);
    fetchSafe(`${MASTER_API}/routings/workcenter-config`, setWorkcenterConfigs);

    // loadRoutingList() intentionally NOT called here — the
    // [currentPage] effect below fires on mount (currentPage starts at 1)
    // and loads page 1.
    loadDrafts();

  }, []);

  const loadDrafts = async () => {
    try {
      const res = await api.get(`${MASTER_API}/routings/get-drafts`);
      if (res.data.success) setDraftRoutings(res.data.drafts || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetches ONE page of routings from the backend (itemsPerPage at a time)
  // instead of all 12k+ at once — this is the actual performance fix.
  // `productCodes`, when non-empty, is forwarded to the backend so attribute
  // filters (Product Type, Construction, Color, ...) narrow the search across
  // ALL routings, not just whatever page is already sitting in the browser.
  const loadRoutingList = async (page = currentPage, productCodes = activeProductCodesRef.current) => {
    try {
      setLoadingList(true);
      // POST body (not a query string): the productCodes list can be thousands of
      // entries long and blew past URL length limits on the production proxy.
      const body = {
        page,
        limit: itemsPerPage,
        productType: activeProductTypeRef.current || "",
        productCodes: productCodes || [],
        hasCodeFilter: activeHasCodeFilterRef.current,
      };
      const res = await api.post(`${MASTER_API}/routings/list`, body);
      // Backend now returns { data, total, page, limit }
      const payload = res.data || {};
      setRoutingList(payload.data || []);
      setListTotal(payload.total || 0);
    } catch (err) {
      console.error(err);
      setRoutingList([]);
      setListTotal(0);
    } finally {
      setLoadingList(false);
    }
  };

  // Refetch whenever the page changes (Previous/Next buttons below).
  useEffect(() => {
    loadRoutingList(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Deletes a routing from the UI list only — the backend now performs a
  // soft delete (status = 'deleted' on routings_data). All child rows
  // (components, activity, parameters, change logs) remain in the DB.
  const handleDeleteRouting = async (routingCode) => {
    if (!canDelete) {
      toast.error("You don't have Delete access. Please contact admin.");
      return;
    }

    if (!routingCode) {
      toast.error("Missing routing code, cannot delete.");
      return;
    }

    const confirmed = window.confirm(
      `Delete routing ${routingCode}? This will hide it from the list.`
    );
    if (!confirmed) return;

    try {
      await api.delete(
        `${MASTER_API}/routings/by-code/${encodeURIComponent(routingCode)}`
      );
      toast.success(`Routing ${routingCode} deleted.`);
      loadRoutingList();
    } catch (err) {
      console.error("Failed to delete routing:", err);
      toast.error("Failed to delete routing. Please try again.");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // FIX 1: loadBomLevel — only look up & store routing IDs in CREATE mode.
  // In EDIT mode we already have the correct editingId set by openFormModal,
  // so we must NOT overwrite productRoutingIdsRef with other product IDs.
  // ─────────────────────────────────────────────────────────────────────────────
  const loadBomLevel = async (productCode) => {
    try {
      const res = await api.get(
        `${MASTER_API}/routings/bom-next-level/${productCode}`
      );

      const data = res.data || {};

      setFormHeader(prev => ({
        ...prev,
        productCode: data.header?.product_code || productCode,
        baseUnit: data.header?.base_unit || "",
        uom: data.header?.uom || "",
        alternative_bom: data.header?.alternative_bom || "",
        product_type: data.header?.product_type || ""
      }));

      const rows = (data.children || []).map((c, index) => ({
        item: index + 1,
        process_id: "",
        sub_process_id: "",
        item_category: c.item_category || "", 
        product_type: c.product_type || "",   
        component_product: c.component_product,
        description: c.component_description,
        quantity: c.quantity ?? "",
        uom: c.uom,
        assembly: c.assembly ?? "",
        valid_from: c.valid_from ? formatDateForInput(c.valid_from) : "",
        valid_to: c.valid_to ? formatDateForInput(c.valid_to) : ""
      }));

      setComponents(rows);
      setCurrentBomChildren(data.children || []);

      // Remember this level's own SFG children so handleSave can walk the
      // full tree (not just the root) when deciding whether everything has
      // been routed.
      sfgMapRef.current[productCode] = rows.filter(isSfgComponent).map(c => c.component_product);

      // Look up whether THIS exact level (productCode) already has a saved
      // routing — in BOTH create and edit mode. This is what makes Save/Update
      // always target the correct record for whichever level is currently on
      // screen (instead of always hitting the very first record the modal was
      // opened with), and it also makes sure each SFG's own saved operations
      // — not blank ones, not the previous level's — are shown when you
      // navigate to it via Next/Back.
      try {
        const existing = await api.get(
          `${MASTER_API}/routings/by-product/${encodeURIComponent(productCode)}`
        );
        const ex = existing.data || {};

        if (ex.routing_id) {
          if (isCreateModeRef.current) {
            productRoutingIdsRef.current[productCode] = ex.routing_id;
          }
          setEditingId(ex.routing_id);
          editingIdRef.current = ex.routing_id;

          setOperations((ex.operations || []).length > 0 ? ex.operations.map(o => ({
            ...o,
            activities: o.activities || { setup_value: "", machine_value: "", labour_value: "", overhead_value: "" },
            machine_parameters: o.machine_parameters || [],
            process_parameters: o.process_parameters || [],
            inspection_parameters: o.inspection_parameters || [],
            processHeaders: o.processHeaders || []
          })) : [emptyOperation()]);
        } else {
          if (isCreateModeRef.current) {
            delete productRoutingIdsRef.current[productCode];
          }
          setEditingId(null);
          editingIdRef.current = null;
          setOperations([emptyOperation()]);
        }
      } catch (lookupErr) {
        if (isCreateModeRef.current) {
          delete productRoutingIdsRef.current[productCode];
        }
        setEditingId(null);
        editingIdRef.current = null;
        setOperations([emptyOperation()]);
      }

    } catch (err) {
      console.error(err);
      toast.error("Failed to load BOM level");
    }
  };

  useEffect(() => {
    api.get(`${MASTER_API}/routings/headers`)
      .then(res => setRoutingHeaders(res.data || []))
      .catch((err) => console.error(err));
  }, []);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const str = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      return str.substring(0, 10);
    }
    const d = new Date(dateStr);
    if (isNaN(d)) return str.substring(0, 10);
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "-";
    const iso = formatDateForInput(dateStr);
    if (!iso || iso.length < 10) return dateStr;
    const [y, m, d] = iso.split("-");
    return `${d}-${m}-${y}`;
  };

  const loadRoutingForViewMode = async (productCode) => {
    try {
      const res = await api.get(`${MASTER_API}/routings/by-product/${encodeURIComponent(productCode)}`);
      const data = res.data || {};
      const hdr = data.header || data;

      // FIX 4: keep editingId/editingIdRef pointed at THIS level's own
      // routing_id while browsing in View mode. Without this, editingId
      // stays stuck on whichever routing the modal was originally opened
      // with (e.g. the top-level FG), so clicking "Edit" then "Save" on a
      // different level (e.g. an SFG) overwrites the WRONG record with
      // this level's data instead of updating/creating its own record.
      if (data.routing_id) {
        setEditingId(data.routing_id);
        editingIdRef.current = data.routing_id;
      } else {
        setEditingId(null);
        editingIdRef.current = null;
      }

      setFormHeader(prev => ({
        ...prev,
        productCode: hdr.productCode || productCode,
        baseUnit: hdr.baseUnit || "",
        uom: hdr.uom || "",
        alternative_bom: hdr.alternative_bom || "",
        product_type: hdr.product_type || ""
      }));

      const ops = data.operations || [];
      setOperations(ops.length > 0 ? ops.map(o => ({
        ...o,
        activities: o.activities || { setup_value: "", machine_value: "", labour_value: "", overhead_value: "" },
        machine_parameters: o.machine_parameters || [],
        process_parameters: o.process_parameters || [],
        inspection_parameters: o.inspection_parameters || [],
        processHeaders: o.processHeaders || []
      })) : [emptyOperation()]);

      const comps = data.components || [];
      setComponents(comps.length > 0 ? comps.map(c => ({
        ...c,
        process_id: c.process_id || "",
        sub_process_id: c.sub_process_id || "",
        valid_from: formatDateForInput(c.valid_from),
        valid_to: formatDateForInput(c.valid_to)
      })) : []);

    } catch (err) {
      console.error("Failed to load routing for view mode:", err);
      alert("No saved routing found for this SFG product. Showing structure only.");

      // Wipe operations so FG operations don't bleed into SFG view
      setOperations([emptyOperation()]);
      setEditingId(null);
      editingIdRef.current = null;

      await loadBomLevel(productCode);
    }
  };

  // Pulls an EXISTING product's saved routing (header/operations/components)
  // into whatever routing is currently open in the modal, without touching
  // the productCode already chosen there. Used by both:
  //   1) the "Copy From" box on Create Routing, and
  //   2) selecting a "Reference Routing" from the dropdown.
  // Always leaves editingId/editingIdRef untouched by the SOURCE routing_id,
  // so Save still creates a brand-new routing for the current product.
  const copyRoutingFromProduct = async (sourceProductCode) => {
    if (!sourceProductCode) return;
    try {
      const res = await api.get(
        `${MASTER_API}/routings/by-product/${encodeURIComponent(sourceProductCode)}`
      );
      const data = res.data || {};
      const hdr = data.header || data;

      if (!hdr || (!hdr.routing_id && !(data.operations || []).length)) {
        toast.error(`No saved routing found for ${sourceProductCode}`);
        return;
      }

      setFormHeader((prev) => ({
        ...prev,
        mainLocation: hdr.mainLocation || prev.mainLocation,
        startDate: hdr.startDate ? formatDateForInput(hdr.startDate) : prev.startDate,
        endDate: hdr.endDate ? formatDateForInput(hdr.endDate) : prev.endDate,
        alternative_bom: hdr.alternative_bom || prev.alternative_bom,
      }));

      const ops = data.operations || [];
      setOperations(ops.length > 0 ? ops.map(o => ({
        ...o,
        id: Date.now() + Math.random(),
        activities: o.activities || { setup_value: "", machine_value: "", labour_value: "", overhead_value: "" },
        machine_parameters: o.machine_parameters || [],
        process_parameters: o.process_parameters || [],
        inspection_parameters: o.inspection_parameters || [],
        processHeaders: o.processHeaders || []
      })) : [emptyOperation()]);

      const comps = data.components || [];
      if (comps.length > 0) {
        setComponents(comps.map(c => ({
          ...c,
          id: Date.now() + Math.random(),
          process_id: c.process_id || "",
          sub_process_id: c.sub_process_id || "",
          valid_from: formatDateForInput(c.valid_from),
          valid_to: formatDateForInput(c.valid_to),
        })));
      }

      // Never let the copy carry over the SOURCE routing_id — this modal
      // must stay in "create a new routing" mode so Save inserts fresh.
      setEditingId(null);
      editingIdRef.current = null;
      if (isCreateModeRef.current) {
        productRoutingIdsRef.current = {};
      }

      toast.success(`Copied routing from ${sourceProductCode}. Review and edit before saving.`);
    } catch (err) {
      console.error("Failed to copy routing:", err);
      toast.error(`No saved routing found for ${sourceProductCode}`);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setRoutingCache({});
    productRoutingIdsRef.current = {};
    setBomLevelStack([]);
    visitedSfgRef.current = {};
    sfgMapRef.current = {};
    setSfgChoices([]);

    // If we got here via a click inside the Routing Tree, go back to the
    // Tree instead of the main listing screen.
    if (openedFromTreeRef.current) {
      openedFromTreeRef.current = false;
      setShowTreeModal(true);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // FIX 2: switchLevel — only use cache in CREATE mode.
  // In EDIT mode, always load fresh from DB so stale operations never bleed.
  // ─────────────────────────────────────────────────────────────────────────────
  const switchLevel = async (targetProduct) => {
    const currentProduct = formHeader.productCode;

    if (isCreateModeRef.current) {
      // CREATE MODE: save current screen into cache, restore target from cache if available
      const updatedCache = {
        ...routingCache,
        [currentProduct]: {
          formHeader: { ...formHeader },
          operations: JSON.parse(JSON.stringify(operations)),
          components: JSON.parse(JSON.stringify(components))
        }
      };
      setRoutingCache(updatedCache);

      if (updatedCache[targetProduct]) {
        // Already visited this level — restore from cache
        const cachedData = updatedCache[targetProduct];
        setFormHeader(cachedData.formHeader);
        setOperations(cachedData.operations);
        setComponents(cachedData.components);
        return;
      }

      // New level in create mode — load fresh BOM
      setOperations([emptyOperation()]);
      setComponents([]);
      await loadBomLevel(targetProduct);

    } else {
      // EDIT MODE: never cache, always load fresh from DB
      // Clear state immediately so old operations don't flash on screen
      setOperations([emptyOperation()]);
      setComponents([]);

      if (isViewing) {
        await loadRoutingForViewMode(targetProduct);
      } else {
        // Edit mode navigating to SFG — load its BOM structure only,
        // do NOT look up routing IDs (handled by isCreateModeRef guard in loadBomLevel)
        await loadBomLevel(targetProduct);
      }
    }
  };


  // ── All listing-page filter keys that participate in cascading (Construction ──
  // ── through Voltage, plus Product Code / Product Description) — same set as BOM. ──
  // ── Product Type is deliberately excluded: it no longer cascades with, or gets ──
  // ── cascaded by, any other filter — see getStaticProductTypeOptions above. ──
  const ATTRIBUTE_FILTER_KEYS = [
    "construction", "code", "cableType", "category",
    "noOfCores", "color", "nominalCrossSection", "referenceOfStandard",
    "voltage", "productCode", "productDescription"
  ];

  // Only filters by a key if that field is actually present on the product records —
  // this prevents a missing/renamed backend column from silently zeroing out all results.
  const productKeys = products.length > 0 ? Object.keys(products[0]) : [];

  const productMatchesFilterSet = (p, filterSet, excludeKey) =>
    Object.entries(filterSet).every(([key, val]) => {
      if (key === excludeKey) return true;
      if (!val) return true;
      if (!productKeys.includes(key)) return true;
      const productVal = String(p[key] || "").trim().toLowerCase();
      const filterVal = String(val.value || "").trim().toLowerCase();
      return productVal === filterVal;
    });

  // Changing one filter narrows every other filter's options. If the newly picked
  // combination no longer matches any product, the least-recently-touched of the
  // OTHER active filters are cleared (one at a time) until the combination is valid
  // again — this keeps all the filters interconnected instead of showing 0 results.
  const handleFilterChange = (key, opt) => {
    setFilters((prev) => {
      let updated = { ...prev, [key]: opt };
      if (products.length === 0) return updated;

      const clearOrder = ATTRIBUTE_FILTER_KEYS.filter((k) => k !== key).reverse();
      let i = 0;
      while (
        i < clearOrder.length &&
        !products.some((p) => productMatchesFilterSet(p, updated, null))
      ) {
        updated = { ...updated, [clearOrder[i]]: null };
        i++;
      }

      // Product Code <-> Product Description auto-fill: picking one fills in
      // the other whenever the pairing is unambiguous.
      if (key === "productCode") {
        if (opt) {
          const match = products.find((p) => String(p.productCode || "").trim() === String(opt.value).trim());
          const desc = match && match.productDescription ? String(match.productDescription).trim() : "";
          updated = { ...updated, productDescription: desc ? { value: desc, label: desc } : null };
        } else {
          updated = { ...updated, productDescription: null };
        }
      } else if (key === "productDescription") {
        if (opt) {
          const matches = products.filter((p) => String(p.productDescription || "").trim() === String(opt.value).trim());
          updated = { ...updated, productCode: matches.length === 1 ? { value: String(matches[0].productCode || "").trim(), label: String(matches[0].productCode || "").trim() } : updated.productCode };
        } else {
          updated = { ...updated, productCode: null };
        }
      }

      return updated;
    });
    setCurrentPage(1);
  };

  // ── Products narrowed down by ALL currently active filters ──
  const filteredProducts = products.filter((p) => productMatchesFilterSet(p, filters, null));

  // ── Product Type is intentionally NOT cascaded — its dropdown always shows every ──
  // ── distinct value found in the full product list, unaffected by any other filter. ──
  const getStaticProductTypeOptions = () => {
    const seen = new Map();
    products
      .map((p) => p.productType)
      .filter((v) => v !== undefined && v !== null && v !== "")
      .map((v) => String(v).trim())
      .filter((v) => v !== "")
      .forEach((v) => {
        const dedupeKey = v.toLowerCase();
        if (!seen.has(dedupeKey)) seen.set(dedupeKey, v);
      });
    return Array.from(seen.values())
      .map((v) => ({ value: v, label: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // ── Cascading options for a given filter field: computed from products that match ──
  // ── every OTHER active filter (so a field's own current selection never hides its ──
  // ── sibling options), restricted to values that actually appear in the data. ──
  const getCascadingOptions = (key) => {
    if (!productKeys.includes(key)) return [];
    const relevant = products.filter((p) => productMatchesFilterSet(p, filters, key));
    // Dedupe case-insensitively (e.g. "Finished Goods" vs "FINISHED GOODS" in the DB
    // should collapse into a single dropdown option). The first-seen casing wins the label.
    const seen = new Map();
    relevant
      .map((p) => p[key])
      .filter((v) => v !== undefined && v !== null && v !== "")
      .map((v) => String(v).trim())
      .filter((v) => v !== "")
      .forEach((v) => {
        const dedupeKey = v.toLowerCase();
        if (!seen.has(dedupeKey)) seen.set(dedupeKey, v);
      });
    return Array.from(seen.values())
      .map((v) => ({ value: v, label: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  // Product Code / Product Description options are further restricted to codes that
  // actually exist in the Routing table. Use `routingHeaders` (a lightweight,
  // unpaginated fetch of every routingCode/productCode) rather than `routingList`
  // here — `routingList` only ever holds the current page (itemsPerPage rows) since
  // /routings is server-paginated, which was why these dropdowns and the "match"
  // count below only ever reflected whatever page happened to be loaded.
  const routingProductCodesInTable = new Set(routingHeaders.map((item) => item.productCode));
  const routingProductDescMap = {};
  products.forEach((p) => { if (p.productCode) routingProductDescMap[p.productCode] = p.productDescription; });

  const routingProductCodeOptions = getCascadingOptions("productCode").filter((o) => routingProductCodesInTable.has(o.value));
  const routingProductDescOptions = getCascadingOptions("productDescription").filter((o) =>
    Array.from(routingProductCodesInTable).some((code) => routingProductDescMap[code] === o.value)
  );

  const hasAttributeFiltersActive = Object.values(filters).some((v) => v);

  // Whenever an attribute filter changes (or the underlying product list first
  // loads), recompute the matching productCodes and re-query the backend from
  // page 1 — this is what makes filtering see ALL routings instead of only the
  // page currently on screen.
  const filtersSignature = JSON.stringify(
    Object.fromEntries(Object.entries(filters).map(([k, v]) => [k, v ? v.value : null]))
  );
  useEffect(() => {
    // Product Type is sent as-is and resolved in SQL. Only the OTHER attribute
    // filters are expanded into a productCodes list.
    const otherFiltersActive = ATTRIBUTE_FILTER_KEYS.some((k) => filters[k]);
    const codes = otherFiltersActive
      ? Array.from(
          new Set(
            products
              .filter((p) => productMatchesFilterSet(p, filters, "productType"))
              .map((p) => p.productCode)
              .filter(Boolean)
          )
        )
      : [];
    activeProductCodesRef.current = codes;
    activeHasCodeFilterRef.current = otherFiltersActive;
    activeProductTypeRef.current = filters.productType ? String(filters.productType.value || "") : "";
    if (currentPage !== 1) {
      setCurrentPage(1); // triggers the [currentPage] effect, which reads the ref above
    } else {
      loadRoutingList(1, codes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSignature, products.length]);

  const filteredRoutings = routingList.filter(r => {
    const matchRfq = selectedRfqFilter ? r.rfq_id === selectedRfqFilter.value : true;
    if (!matchRfq) return false;
    // Attribute filtering now happens server-side (see the effect above and
    // loadRoutingList's `productCodes` param) — routingList already only
    // contains matching rows, across ALL pages, not just this one.
    return true;
  });

  // ── Routing Tree: fetch + render, mirroring the BOM tree modal ──
  const toggleNode = (code) => {
    setExpandedNodes((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const openRoutingTree = async (productCode) => {
    try {
      const res = await api.get(`${MASTER_API}/routings/tree/${productCode}`);
      setRoutingTree(res.data.data || res.data);
      setShowTreeModal(true);
    } catch (err) {
      toast.error("Failed to load Routing tree");
    }
  };

  const renderRoutingNode = (node, level = 0) => {
    if (!node) return null;

    const hasChildren = node.children && node.children.length > 0;
    const expanded = expandedNodes[node.code] ?? true;

    return (
      <div key={node.code} style={{ marginLeft: level * 25, marginTop: 6 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 12px",
            background: level === 0 ? "#dbeafe" : "#ffffff",
            border: "1px solid #d1d5db",
            borderRadius: 6,
            boxShadow: "0 2px 5px rgba(0,0,0,0.08)"
          }}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleNode(node.code)}
              style={{
                width: 20, height: 20, fontSize: 12,
                border: "none", borderRadius: "50%",
                background: "#2563eb", color: "white", cursor: "pointer"
              }}
            >
              {expanded ? "-" : "+"}
            </button>
          ) : (
            <span style={{ fontSize: 20 }}>•</span>
          )}

          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                color: node.routing_id ? "#2563eb" : undefined,
                cursor: node.routing_id ? "pointer" : "default",
                textDecoration: node.routing_id ? "underline" : "none",
                width: "fit-content"
              }}
              title={node.routing_id ? "View Routing for this product" : "No Routing found for this product"}
              onClick={() => {
                if (!node.routing_id) {
                  toast.error("No Routing found for this product");
                  return;
                }
                openedFromTreeRef.current = true;
                setShowTreeModal(false);
                openFormModal(node.routing_id, "view");
              }}
            >
              {node.code}
            </div>
            <div style={{ fontSize: 10, color: "#666" }}>
              {node.description || "No Description"}
            </div>
            {(node.quantity || node.base_unit || node.uom) && (
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                {node.quantity !== undefined && node.quantity !== null && node.quantity !== "" && (
                  <>Qty: <strong style={{ color: "#334155" }}>{node.quantity}</strong></>
                )}
                {node.base_unit && (
                  <>{node.quantity ? "  •  " : ""}Base Unit: <strong style={{ color: "#334155" }}>{node.base_unit}</strong></>
                )}
                {node.uom && (
                  <>{(node.quantity || node.base_unit) ? "  •  " : ""}UOM: <strong style={{ color: "#334155" }}>{node.uom}</strong></>
                )}
              </div>
            )}
          </div>

          {hasChildren && (
            <span style={{ background: "#e0f2fe", padding: "2px 8px", borderRadius: 20, fontSize: 10 }}>
              {node.children.length} items
            </span>
          )}
        </div>

        {expanded && hasChildren && node.children.map((child) => renderRoutingNode(child, level + 1))}
      </div>
    );
  };

  // routingList already contains just this one page (itemsPerPage rows)
  // from the backend, so no further slicing is needed here — but the
  // page COUNT must come from the backend's true total, not from the
  // (already-tiny) local array, or Next/Previous would break.
  const currentItems = filteredRoutings;
  const totalPages = Math.max(1, Math.ceil(listTotal / itemsPerPage));

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleHeaderChange = (field, value) => setFormHeader((h) => ({ ...h, [field]: value }));

  const addOperationRow = () => setOperations((prev) => [...prev, emptyOperation()]);
  const removeOperationRow = (idx) => setOperations((prev) => prev.filter((_, i) => i !== idx));
  const updateOperationField = (idx, field, value) => setOperations((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));

  const handleProcessIdSelect = async (idx, processId) => {
    const match = workcenterConfigs.find(c => c.process_id === processId);
    const processName = match ? match.process_name : "";

    const directMatch = workcenterConfigs.find(
      c => c.process_id === processId && (!c.sub_process_id || c.sub_process_id.trim() === "")
    );

    let workcenterName = "";
    let workcenterDesc = "";
    let mParams = [];

    if (directMatch && directMatch.workcenter_name) {
      workcenterName = directMatch.workcenter_name;
      workcenterDesc = directMatch.workcenter_description || "";

      try {
        const res = await api.get(
          `${MASTER_API}/routings/classification/${encodeURIComponent(workcenterName)}`
        );
        const classifications = res.data || [];
        mParams = classifications.map(c => ({
          name: c.workcenterclassification || "",
          value: "",
          uom: c.workcenterclassificationuom || ""
        }));
      } catch (err) {
        console.error(err);
      }
    }

    let pParams = [];
    try {
      const res = await api.get(
        `${MASTER_API}/routings/parameters?process=${encodeURIComponent(processName)}`);
      pParams = res.data || [];
    } catch (err) {
      console.error(err);
    }

    let iParams = [];
    if (processId) {
      try {
        const productType = formHeaderRef.current.product_type || formHeaderRef.current.productType || "";
        const res = await api.get(
          `${MASTER_API}/routings/inspection-characteristics?process_id=${encodeURIComponent(processId)}&product_type=${encodeURIComponent(productType)}`
        );
        iParams = res.data || [];
      } catch (err) {
        console.error("Failed to fetch inspection characteristics", err);
      }
    }

    setOperations(prev => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        process_id: processId,
        process_name: processName,
        sub_process_id: "",
        sub_process_name: "",
        workcenterName: workcenterName,
        workcenter_description: workcenterDesc,
        machine_parameters: mParams.length ? mParams : updated[idx].machine_parameters,
        process_parameters: pParams,
        // FIX: same-process re-select (e.g. switch away then back to
        // "Insulation") must not wipe out inspection_parameters that were
        // already loaded for this operation just because this particular
        // fetch came back empty (stale product_type, slow request, etc).
        // Only replace when we actually got data back.
        inspection_parameters: iParams.length > 0 ? iParams : updated[idx].inspection_parameters,
        processHeaders: []
      };
      return updated;
    });
  };

  const handleSubProcessIdSelect = async (idx, subProcessId) => {
    const op = operations[idx];
    const match = workcenterConfigs.find(
      c => c.process_id === op.process_id && c.sub_process_id === subProcessId
    );
    const subProcessName = match ? match.sub_process_name : "";
    const workcenterName = match ? (match.workcenter_name || "") : "";
    const workcenterDesc = match ? (match.workcenter_description || "") : "";

    let mParams = [];
    if (workcenterName) {
      try {
        const res = await api.get(
          `${MASTER_API}/routings/classification/${encodeURIComponent(workcenterName)}`
        );
        const classifications = res.data || [];
        mParams = classifications.map(c => ({
          name: c.workcenterclassification || "",
          value: "",
          uom: c.workcenterclassificationuom || ""
        }));
      } catch (err) {
        console.error(err);
      }
    }

    let processHeaders = [];
    if (op.process_id && subProcessId) {
      try {
        const res = await api.get(
          `${MASTER_API}/routings/parameters/${op.process_id}/${subProcessId}`
        );
        processHeaders = res.data || [];
      } catch (err) {
        console.error(err);
      }
    }

    let pParams = [];
    if (op.process_id && subProcessId) {
      try {
        const res = await api.get(
          `${MASTER_API}/routings/parameters?process=${encodeURIComponent(op.process_name)}&subprocess=${encodeURIComponent(subProcessName)}`);
        pParams = res.data || [];
      } catch (err) {
        console.error(err);
      }
    }

    let iParams = [];
    if (op.process_id) {
      try {
        const productType = formHeaderRef.current.product_type || formHeaderRef.current.productType || "";
        const res = await api.get(
          `${MASTER_API}/routings/inspection-characteristics?process_id=${encodeURIComponent(op.process_id)}&sub_process_id=${encodeURIComponent(subProcessId)}&product_type=${encodeURIComponent(productType)}`
        );
        iParams = res.data || [];
      } catch (err) {
        console.error("Failed to fetch inspection characteristics", err);
      }
    }

    setOperations(prev => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        sub_process_id: subProcessId,
        sub_process_name: subProcessName,
        workcenterName,
        workcenter_description: workcenterDesc,
        machine_parameters: mParams.length ? mParams : updated[idx].machine_parameters,
        process_parameters: pParams.length > 0 ? pParams : updated[idx].process_parameters,
        inspection_parameters: iParams.length > 0 ? iParams : updated[idx].inspection_parameters,
        processHeaders
      };
      return updated;
    });
  };

  const updateActivityValue = (opIdx, field, val) => {
    setOperations(prev => {
      const updated = [...prev];
      updated[opIdx] = { ...updated[opIdx], activities: { ...updated[opIdx].activities, [field]: val } };
      return updated;
    });
  };

  const updateParameterValue = (opIdx, type, paramIdx, val) => {
    setOperations(prev => {
      const updated = [...prev];
      const params = [...updated[opIdx][type]];
      params[paramIdx] = { ...params[paramIdx], value: val };
      updated[opIdx] = { ...updated[opIdx], [type]: params };
      return updated;
    });
  };

  const addComponentRow = () => setComponents((prev) => [...prev, emptyComponent()]);
  const removeComponent = (idx) => setComponents((prev) => prev.filter((_, i) => i !== idx));
  const updateComponentField = (idx, field, value) => setComponents((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post(`${MASTER_API}/routings/parse-excel`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data.success) {
        loadDrafts();
        setShowDraftModal(true);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to upload Excel file");
    } finally {
      e.target.value = null;
    }
  };

  const handlePublishDraft = async (draft_routing_id) => {

    if (!canApprove) {
      toast.error("You don't have Approve access.Please contact admin.");
      return;
    }
    setPublishingId(draft_routing_id);
    try {
      const res = await api.post(`${MASTER_API}/routings/publish-draft`, { draft_routing_id });
      if (res.data.success) {
        alert(res.data.message);
        loadDrafts();
        loadRoutingList();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to publish draft");
    } finally {
      setPublishingId(null);
    }
  };

  const handlePublishAllDrafts = async () => {
    if (!canApprove) {
      toast.error("You don't have Approve access.Please contact admin.");
      return;
    }
    if (draftRoutings.length === 0) return;
    setIsPublishingAll(true);
    try {
      for (const draft of draftRoutings) {
        await api.post(`${MASTER_API}/routings/publish-draft`, { draft_routing_id: draft.routing_id });
      }
      alert("All Routings Saved to Main Table Successfully!");
      loadDrafts();
      loadRoutingList();
      setShowDraftModal(false);
    } catch (err) {
      console.error(err);
      alert("An error occurred while saving all drafts. Some may have failed.");
    } finally {
      setIsPublishingAll(false);
    }
  };

  const handleNextBom = async () => {
    if (!components.length) {
      toast.info("No components found.");
      return;
    }

    const allSfgs = components.filter(isSfgComponent);

    if (!allSfgs.length) {
      toast.info("No Semi-Finished component found to route to next level.");
      return;
    }

    const currentProduct = formHeader.productCode;

    // ── SIBLING CHECK ─────────────────────────────────────────────────────────
    // If we are already inside a child SFG (bomLevelStack.length > 1), the user
    // is trying to go DEEPER into this SFG's own children.  Before allowing that,
    // every SIBLING SFG at the PARENT level must have been visited / routed first.
    if (bomLevelStack.length > 1) {
      const parentProduct = bomLevelStack[bomLevelStack.length - 2];
      const siblingSfgs = sfgMapRef.current[parentProduct] || [];
      const visitedAtParent = visitedSfgRef.current[parentProduct] || [];
      const unfinishedSiblings = siblingSfgs.filter(
        (s) => s !== currentProduct && !visitedAtParent.includes(s)
      );

      if (unfinishedSiblings.length > 0) {
        alert(
          `Please complete routing for all sibling SFGs before going deeper.\n` +
          `Pending: ${unfinishedSiblings.join(", ")}`
        );
        return;
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    const parentProduct = currentProduct;
    const visited = visitedSfgRef.current[parentProduct] || [];

    const remaining = allSfgs.filter(
      (c) => !visited.includes(c.component_product)
    );

    if (!remaining.length) {
      toast.info("All Semi-Finished components at this level have already been routed.");
      return;
    }

    if (remaining.length === 1) {
      await goToSfg(parentProduct, remaining[0].component_product);
      return;
    }

    setSfgChoices(remaining);
  };

  const goToSfg = async (parentProduct, nextProduct) => {
    visitedSfgRef.current[parentProduct] = [
      ...(visitedSfgRef.current[parentProduct] || []),
      nextProduct
    ];
    setSfgChoices([]);

    setBomLevelStack((prev) => [...prev, nextProduct]);

    await switchLevel(nextProduct);
  };

  const handleBackBom = async () => {
    if (bomLevelStack.length <= 1) return;

    const newStack = [...bomLevelStack];
    newStack.pop();
    const previousProductCode = newStack[newStack.length - 1];

    // NOTE: we deliberately do NOT remove the popped SFG from
    // visitedSfgRef here. Its data is already cached by switchLevel, so
    // going Back must not make Save (or the Next-level choice list) think
    // that SFG's routing is incomplete again.

    setBomLevelStack(newStack);
    await switchLevel(previousProductCode);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // FIX 3: handleSave EDIT MODE — use editingIdRef (not productRoutingIdsRef)
  // as the source of truth for which record to update.
  // productRoutingIdsRef is only reliable in create mode.
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editingId && !canCreate) {
      toast.error("You don't have Create access.Please contact admin.");
      return;
    }

    if (editingId && !canEdit) {
      toast.error("You don't have Edit access.Please contact admin.");
      return;
    }
    if (!formHeader.productCode) return toast.error("Select product code");
    if (!operations || operations.length === 0) return toast.error("Add at least one operation");

    // ─────────────────────────────────────────────────────────────────────────
    // Block Save in CREATE mode until every SFG component on the root BOM level
    // has been routed. Save in create mode submits the WHOLE tree at once, so
    // an incomplete tree (e.g. 1 of 3 SFGs done) must not be allowed through.
    // ─────────────────────────────────────────────────────────────────────────
    if (isCreateModeRef.current) {
      const rootProduct = bomLevelStack[0] || formHeader.productCode;

      // Walk the WHOLE BOM tree — not just the root's direct children — and
      // collect every Semi-Finished component, at any depth, that hasn't had
      // its own routing completed yet.
      const collectPendingSfgs = (productCode, seen = new Set()) => {
        if (seen.has(productCode)) return []; // guard against any accidental cycles
        seen.add(productCode);

        const children = sfgMapRef.current[productCode] || [];
        const visited = visitedSfgRef.current[productCode] || [];
        let pending = [];

        children.forEach((child) => {
          if (!visited.includes(child)) {
            pending.push(child);
          } else {
            // This child was routed — but check ITS children too.
            pending = pending.concat(collectPendingSfgs(child, seen));
          }
        });

        return pending;
      };

      const pending = collectPendingSfgs(rootProduct);
    }

    setSaving(true);
    try {

      if (isCreateModeRef.current) {
        // ── CREATE MODE ──────────────────────────────────────────────────────────
        // Save ALL levels in the cache + the current level on screen.
        const completePayload = {
          ...routingCache,
          [formHeader.productCode]: {
            formHeader: { ...formHeader },
            operations: JSON.parse(JSON.stringify(operations)),
            components: JSON.parse(JSON.stringify(components))
          }
        };

        for (const productKey of Object.keys(completePayload)) {
          const targetData = completePayload[productKey];

          const fd = new FormData();
          Object.keys(targetData.formHeader).forEach(k => fd.append(k, targetData.formHeader[k] || ""));
          fd.append("operations", JSON.stringify(targetData.operations));
          fd.append("components", JSON.stringify(targetData.components.map(c => ({
            ...c,
            process_id: c.process_id || null,
            sub_process_id: c.sub_process_id || null
          }))));
          documents.forEach((f) => fd.append("documents", f));

          const existingRoutingId = productRoutingIdsRef.current[productKey] || null;

          if (existingRoutingId && !isDraftView) {
            await api.put(`${MASTER_API}/routings/update/${existingRoutingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } else {
            try {
              const res = await api.post(`${MASTER_API}/routings/add`, fd, { headers: { "Content-Type": "multipart/form-data" } });
              if (res.data?.routing_id) {
                productRoutingIdsRef.current[productKey] = res.data.routing_id;
              }
            } catch (addErr) {
              if (addErr?.response?.status === 409) {
                toast.error(addErr.response.data?.message || `A routing already exists for product ${productKey}. Please update it instead.`);
                throw addErr;
              }
              throw addErr;
            }
          }
        }

        toast.success("All multi-level product routings saved successfully!");

      } else {
        // ── EDIT MODE ────────────────────────────────────────────────────────────
        // Save ONLY the current level on screen.
        // Use editingIdRef as source of truth — it was set by openFormModal and
        // is the routing_id of the record the user clicked "Edit" on.
        // Do NOT use productRoutingIdsRef here — it may be empty or polluted.
        const fd = new FormData();
        Object.keys(formHeader).forEach(k => fd.append(k, formHeader[k] || ""));
        fd.append("operations", JSON.stringify(operations));
        fd.append("components", JSON.stringify(components.map(c => ({
          ...c,
          process_id: c.process_id || null,
          sub_process_id: c.sub_process_id || null
        }))));
        documents.forEach((f) => fd.append("documents", f));

        // ✅ Use editingIdRef — the original routing_id from openFormModal
        const existingRoutingId = editingIdRef.current || null;

        if (existingRoutingId && !isDraftView) {
          await api.put(`${MASTER_API}/routings/update/${existingRoutingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } else {
          try {
            const res = await api.post(`${MASTER_API}/routings/add`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (res.data?.routing_id) {
              setEditingId(res.data.routing_id);
              editingIdRef.current = res.data.routing_id;
            }
          } catch (addErr) {
            if (addErr?.response?.status === 409) {
              toast.error(addErr.response.data?.message || `A routing already exists for this product. Please update it instead.`);
              throw addErr;
            }
            throw addErr;
          }
        }

        toast.success("Routing saved successfully!");
      }

      setShowModal(false);
      setEditingId(null);
      editingIdRef.current = null;
      setIsViewing(false);
      productRoutingIdsRef.current = {};
      setRoutingCache({});
      loadRoutingList();
      loadDrafts();
    } catch (err) {
      console.error("[handleSave] failed:", err);
      toast.error("Failed to save routing");
    } finally {
      setSaving(false);
    }
  };

  const openFormModal = async (id, mode = 'view') => {
    setRoutingCache({});
    productRoutingIdsRef.current = {};
    isCreateModeRef.current = false;  // editing an existing routing — save only current level
    setBomLevelStack([]);
    visitedSfgRef.current = {};
    sfgMapRef.current = {};
    setSfgChoices([]);

    setEditingId(id);
    editingIdRef.current = id; // ✅ keep ref in sync immediately
    setIsViewing(mode === 'view');
    setRoutingLogs([]); 

    try {
      const endpoint = mode === 'draft' ? `drafts/${id}` : `${id}`;
      // const res = await api.get(`${MASTER_API}/routings/${endpoint}`);
      const res = await api.get(`${MASTER_API}/routings/${endpoint}`);
      const data = res.data || {};
      const hdr = data.header || data;

      // In edit mode, only store the ONE routing being edited — never more.
      // This prevents Save from accidentally updating other products.
      if (hdr.productCode) {
        productRoutingIdsRef.current = { [hdr.productCode]: id };
      }

      if (mode !== 'draft') {
        try {
          const logRes = await api.get(`${MASTER_API}/routings/change-logs/${id}`);
          setRoutingLogs(logRes.data || []);
        } catch (err) {
          console.error("No change logs found or failed to fetch:", err);
        }
      }

      setFormHeader({
        productCode: hdr.productCode || "",
        mainLocation: hdr.mainLocation || "",
        startDate: hdr.startDate ? formatDateForInput(hdr.startDate) : "",
        endDate: hdr.endDate ? formatDateForInput(hdr.endDate) : "",
        baseUnit: hdr.baseUnit || "",
        uom: hdr.uom || "",
        alternative_bom: hdr.alternative_bom || "",
        reference_routing: hdr.reference_routing || "",
        // FIX: was missing here entirely, so even after the backend started
        // returning product_type correctly, openFormModal (the function
        // actually wired to the Edit button) dropped it on the floor before
        // it ever reached state. Trimmed defensively since the source data
        // can carry stray whitespace (e.g. "Semi Finished ").
        product_type: (hdr.product_type || "").trim(),
      });

      setBomLevelStack([hdr.productCode || ""]);

      const ops = data.operations || [];
      if (ops.length > 0) {
        setOperations(ops.map(o => ({
          ...o,
          id: o.id || Date.now() + Math.random(),
          activities: o.activities || {
            setup_value: "", setup_uom: "",
            machine_value: "", machine_uom: "",
            labour_value: "", labour_uom: "",
            overhead_value: "", overhead_uom: ""
          },
          machine_parameters: o.machine_parameters || [],
          inspection_parameters: o.inspection_parameters || [],
          processHeaders: o.processHeaders || []
        })));
      } else {
        setOperations([emptyOperation()]);
      }

      const comps = data.components || [];
      if (comps.length > 0) {
        setComponents(comps.map(c => ({
          ...c,
          id: c.id || Date.now() + Math.random(),
          process_id: c.process_id || "",
          sub_process_id: c.sub_process_id || "",
          valid_from: formatDateForInput(c.valid_from),
          valid_to: formatDateForInput(c.valid_to),
        })));
      } else {
        setComponents([emptyComponent()]);
      }

      setShowModal(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Routing details");
    }
  };

  const uniqueProcessIds = Array.from(
    new Map(
      workcenterConfigs
        .filter(c => c.process_id)
        .map(c => [c.process_id, { process_id: c.process_id, process_name: c.process_name }])
    ).values()
  );

  const getSubProcessOptions = (processId) => {
    if (!processId) return [];
    const seen = new Map();
    workcenterConfigs
      .filter(c => c.process_id === processId && c.sub_process_id)
      .forEach(c => {
        if (!seen.has(c.sub_process_id)) {
          seen.set(c.sub_process_id, { sub_process_id: c.sub_process_id, sub_process_name: c.sub_process_name });
        }
      });
    return Array.from(seen.values());
  };

  const getWc = (wcName) => workcenters.find(w => w.workcenter_name === wcName) || {};

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        {/* ===================== PAGE HEADER ===================== */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#1e293b' }}>Routing</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>Manage and view product routing</div>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/* <button
              className="btn btn-outline-dark"
              style={{ padding: "8px 16px", borderRadius: 4, cursor: "pointer", fontWeight: 600, border: "1px solid #1e293b", background: "white", color: "#1e293b", fontSize: "13px" }}
              onClick={() => {
                if (!canCreate) {
                  toast.error("You don't have Create access.Please contact admin.");
                  return;
                }
                setShowUploadModal(true);
              }}
            >
              ☁️ Create Routing By Upload
            </button> */}
            <button className="btn btn-dark"
              onClick={() => {
                if (!canCreate) {
                  toast.error("You don't have Create access.Please contact admin.");
                  return;
                }

                loadedBomRef.current = { productCode: null, altBom: null };
                productRoutingIdsRef.current = {};
                isCreateModeRef.current = true;
                setBomLevelStack([]);
                setRoutingCache({});
                visitedSfgRef.current = {};
                sfgMapRef.current = {};
                setSfgChoices([]);
                setEditingId(null);
                editingIdRef.current = null;
                setIsViewing(false);
                setFormHeader({
                  productCode: "",
                  mainLocation: "",
                  startDate: "",
                  endDate: "",
                  alternative_bom: "",
                  reference_routing: ""
                });
                setOperations([emptyOperation()]);
                setComponents([]);
                setShowModal(true);
                setIsDraftView(false);
              }}
              style={{ padding: "8px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: "13px" }}
            >
              + Create Routing
            </button>
          </div>
        </div>

        {/* FILTERS — same cascading structure as the BOM listing page */}
        <div style={{ background: '#ffffff', borderRadius: 8, padding: 15, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 }}>

          {/* Row 1: Product Type */}
          <div className="row mb-3">
            <div className="col-md-3">
              <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block', color: '#334155' }}>Product Type</label>
              <Select
                options={getStaticProductTypeOptions()}
                value={filters.productType}
                onChange={(opt) => handleFilterChange("productType", opt)}
                placeholder="Select Product Type"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customSelectStyles}
              />
            </div>
          </div>

          {/* Row 2: Construction, Code, Cable Type, Category, Cores, Color, Cross Section, Std. Ref, Voltage */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 18 }}>
            {[
              { key: "construction", label: "Construction" },
              { key: "code", label: "Code" },
              { key: "cableType", label: "Cable Type" },
              { key: "category", label: "Category" },
              { key: "noOfCores", label: "Cores" },
              { key: "color", label: "Color" },
              { key: "nominalCrossSection", label: "Cross Section" },
              { key: "referenceOfStandard", label: "Std. Ref" },
              { key: "voltage", label: "Voltage" },
            ].map(({ key, label }) => (
              <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                <label style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block', color: '#334155' }}>{label}</label>
                <Select
                  placeholder={label}
                  isClearable
                  options={getCascadingOptions(key)}
                  value={filters[key]}
                  onChange={(opt) => handleFilterChange(key, opt)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={customSelectStyles}
                />
              </div>
            ))}
          </div>

          {/* Row 3: Product Code and Product Description — cascade with every filter above, and with each other */}
          <div className="row g-3">
            <div className="col-md-4">
              <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block', color: '#334155' }}>Product Code</label>
              <Select
                options={routingProductCodeOptions}
                value={filters.productCode}
                onChange={(opt) => handleFilterChange("productCode", opt)}
                placeholder="Search or Select Product..."
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customSelectStyles}
              />
            </div>
            <div className="col-md-4">
              <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block', color: '#334155' }}>Product Description</label>
              <Select
                options={routingProductDescOptions}
                value={filters.productDescription}
                onChange={(opt) => handleFilterChange("productDescription", opt)}
                placeholder="Product Description..."
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customSelectStyles}
              />
            </div>
          </div>

          {/* Now always the true count across ALL pages — filtering happens
              server-side (see loadRoutingList's productCodes param), so
              listTotal reflects the active filters, not just this page. */}
          <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: "#16a34a", display: "flex", alignItems: "center", gap: 14 }}>
            <span>{listTotal} {listTotal === 1 ? "product" : "products"} match</span>
            <button
              onClick={checkOrphanRoutings}
              disabled={loadingOrphans}
              title="Find routings whose product code is missing from the Product Master"
              style={{ background: "none", border: "none", padding: 0, color: "#b45309", fontWeight: 600, fontSize: 12, cursor: loadingOrphans ? "not-allowed" : "pointer", textDecoration: "underline" }}
            >
              {loadingOrphans ? "Checking..." : "⚠ Check for routings missing from Product Master"}
            </button>
          </div>
        </div>

        {/* MAIN LIST TABLE */}
        <div style={{ width: "100%", marginTop: 10, background: "white", borderRadius: 6, overflowX: "auto", fontSize: "13px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f1f1f1" }}>
                <th style={thStyle}>Routing Id</th>
                <th style={thStyle}>Product Code</th>
                <th style={thStyle}>Product Description</th>
                <th style={thStyle}>Base Unit</th>
                <th style={thStyle}>UOM</th>
                <th style={thStyle}>Alternative BOM</th>
                <th style={thStyle}>Location</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {loadingList ? (
                <tr>
                  <td colSpan={8} style={{ padding: 20, textAlign: "center" }}>Loading...</td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#777" }}>No Routing Records Found</td>
                </tr>
              ) : (
                currentItems.map((r) => (
                  <tr key={r.routing_id}>
                    <td style={tdStyle}>{r.routingCode}</td>
                    <td style={tdStyle}>{r.productCode}</td>
                    <td style={tdStyle}>{routingProductDescMap[r.productCode] || "-"}</td>
                    <td style={tdStyle}>{r.baseUnit || "-"}</td>
                    <td style={tdStyle}>{r.uom || "-"}</td>
                    <td style={tdStyle}>{r.alternative_bom || "-"}</td>
                    <td style={tdStyle}>{r.mainLocation || "-"}</td>
                    <td style={tdStyle}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <button
                          className="btn btn-outline-dark btn-sm"
                          style={{ fontSize: "11px", padding: "2px 8px" }}
                          onClick={() => {
                            if (!canView) {
                              toast.error("You don't have View access.");
                              return;
                            }
                            openFormModal(r.routing_id, "view");
                          }}
                        >
                          View
                        </button>

                        <button
                          className="btn btn-outline-success btn-sm"
                          style={{ fontSize: "11px", padding: "2px 8px" }}
                          onClick={() => openRoutingTree(r.productCode)}
                        >
                          Tree
                        </button>

                        <button
                          title="Delete"
                          style={{
                            background: "#fff",
                            border: "1px solid #f5c2c7",
                            borderRadius: "4px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "4px 8px",
                            cursor: "pointer"
                          }}
                          onClick={() => handleDeleteRouting(r.routingCode)}
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: '15px', gap: '10px' }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '4px', background: currentPage === 1 ? '#f1f5f9' : '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '13px', color: '#334155' }}
            >
              Previous
            </button>
            <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: '4px', background: currentPage === totalPages ? '#f1f5f9' : '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '13px', color: '#334155' }}
            >
              Next
            </button>
          </div>
        )}

        {/* ===================== FLOATING DRAFTS BADGE ===================== */}
        {/* <div style={{ position: 'fixed', bottom: 30, right: 30, display: 'flex', flexDirection: 'column', gap: 15, zIndex: 9999 }}>
          {draftRoutings.length > 0 && (
            <div
              onClick={() => setShowDraftModal(true)}
              style={{
                background: '#1e293b', color: '#fff', padding: '12px 20px', borderRadius: 50,
                cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.3s ease'
              }}
            >
              <span style={{ fontWeight: 'bold', fontSize: 15 }}>📂 Pending Drafts</span>
              <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>
                {draftRoutings.length}
              </span>
            </div>
          )}
        </div> */}

        {/* ===================== MAIN CREATE / VIEW / EDIT MODAL ===================== */}
        {showModal && ReactDOM.createPortal(
          <>
            <div onClick={closeModal} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 10002 }} />
            <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "95vw", height: "95vh", overflowY: "hidden", display: "flex", flexDirection: "column", background: "white", zIndex: 10003, borderRadius: 12, padding: 20, boxShadow: "0 0 20px rgba(0,0,0,0.3)", fontSize: "12px" }}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0 }}>{isDraftView ? `Draft Routing (${editingId})` : editingId ? (isViewing ? "View Routing" : "Edit Routing") : "Create Routing"}</h3>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => {
                      if (!canEdit) {
                        toast.error("You don't have Edit access. Please contact admin.");
                        return;
                      }
                      // editingIdRef.current is already set to the CURRENT level's
                      // routing_id by loadRoutingForViewMode — do NOT reset it here.
                      // Just flip the UI into edit mode.
                      setIsViewing(false);
                    }}
                    style={{
                      padding: "6px 14px",
                      background: "#1976d2",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontWeight: 600
                    }}
                  >
                    Edit
                  </button>
                  <button onClick={closeModal} style={{ background: "#bbb", border: "none", padding: "6px 12px", borderRadius: 6, cursor: "pointer" }}>Close</button>
                </div>
              </div>
              <hr style={{ margin: "12px 0" }} />

              <div style={{ flex: 1, overflowY: "auto", marginTop: 15, paddingRight: 5 }}>
                {/* HEADER FIELDS */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, flexShrink: 0 }}>
                  <div style={{ minWidth: "200px" }}>
                    <label style={smallLabel}>Product Code</label>
                    <Select
                      styles={customSelectStyles}
                      options={products.map(p => {
                        const code = p.productCode || p.productcode;
                        const desc = p.productDescription || "";
                        return {
                          value: code,
                          label: desc ? `${code} - ${desc}` : code,
                          uom: p.uom
                        };
                      })}
                      value={
                        formHeader.productCode
                          ? {
                            value: formHeader.productCode,
                            label: (() => {
                              const p = products.find(x => String(x.productCode || x.productcode) === String(formHeader.productCode));
                              const desc = p?.productDescription || "";
                              return desc ? `${formHeader.productCode} - ${desc}` : formHeader.productCode;
                            })()
                          }
                          : null
                      }
                      onChange={(selected) => {

                        const val =
                          selected ? selected.value : "";

                        if (!val) return;

                        setBomLevelStack([val]);
                        visitedSfgRef.current = {};
                        sfgMapRef.current = {};
                        setSfgChoices([]);

                        loadBomLevel(val);

                      }}
                      isDisabled={isViewing}
                      placeholder="Select"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div><label style={smallLabel}>Base Unit</label><input style={smallInput} value={formHeader.baseUnit || ""} onChange={(e) => handleHeaderChange("baseUnit", e.target.value)} readOnly={isViewing} /></div>

                  <div><label style={smallLabel}>UOM</label><input style={{ ...smallInput, background: "#f5f5f5" }} value={formHeader.uom || ""} readOnly /></div>

                  <div>
                    <label style={smallLabel}>Alternative BOM</label>
                    <Select
                      styles={customSelectStyles}
                      options={bomAlternatives.map(b => ({ value: String(b.alternative_bom ?? b), label: String(b.alternative_bom ?? b) }))}
                      value={formHeader.alternative_bom ? { value: String(formHeader.alternative_bom), label: String(formHeader.alternative_bom) } : null}
                      onChange={(selected) => handleHeaderChange("alternative_bom", selected ? selected.value : "")}
                      isDisabled={isViewing}
                      placeholder="Select"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div>
                    <label style={smallLabel}>Location</label>
                    <Select
                      styles={customSelectStyles}
                      options={plants.map((pl) => ({ value: pl.name, label: pl.name }))}
                      value={formHeader.mainLocation ? { value: formHeader.mainLocation, label: formHeader.mainLocation } : null}
                      onChange={(selected) => handleHeaderChange("mainLocation", selected ? selected.value : "")}
                      isDisabled={isViewing}
                      placeholder="Select"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>

                  <div><label style={smallLabel}>Valid From</label><input type="date" style={smallInput} value={formHeader.startDate} onChange={(e) => handleHeaderChange("startDate", e.target.value)} readOnly={isViewing} /></div>

                  <div><label style={smallLabel}>Valid To</label><input type="date" style={smallInput} value={formHeader.endDate} onChange={(e) => handleHeaderChange("endDate", e.target.value)} readOnly={isViewing} /></div>

                  <div>
                    <label style={smallLabel}>Reference Routing</label>
                    <Select
                      styles={customSelectStyles}
                      options={routingHeaders.map((h) => ({ value: h.routingCode, label: `${h.routingCode} - ${h.productCode}`, productCode: h.productCode }))}
                      value={
                        formHeader.reference_routing
                          ? {
                            value: formHeader.reference_routing,
                            label: (() => {
                              const h = routingHeaders.find(rh => rh.routingCode === formHeader.reference_routing);
                              return h ? `${h.routingCode} - ${h.productCode}` : formHeader.reference_routing;
                            })()
                          }
                          : null
                      }
                      onChange={(selected) => {
                        handleHeaderChange("reference_routing", selected ? selected.value : "");
                        // Selecting a reference routing pulls its full
                        // operations/components/header into this modal so
                        // the user can review and edit before saving.
                        if (selected && selected.productCode) {
                          copyRoutingFromProduct(selected.productCode);
                        }
                      }}
                      isDisabled={isViewing}
                      placeholder="Select Reference Routing"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                {/* OPERATIONS SUB-HEADER */}
                <div style={{ marginTop: 20, background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", flexShrink: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <h4 style={{ margin: 0, color: "#0f172a" }}>Operations</h4>
                    {!isViewing && <button onClick={() => {
                      if (!canEdit) {
                        toast.error("You don't have Edit access. Please contact admin.");
                        return;
                      }
                      addOperationRow();
                    }} style={{ padding: "6px 12px", background: "#1976d2", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}>+ Add Operation</button>}
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr style={{ background: "#e2e8f0" }}>
                        <th style={thStyle}>Process</th><th style={thStyle}>Process Name</th><th style={thStyle}>Subprocess ID</th><th style={thStyle}>Subprocess Name</th><th style={thStyle}>Workcenter</th><th style={thStyle}>WC Desc</th><th style={thStyle}></th>
                      </tr></thead>
                      <tbody>
                        {operations.map((op, i) => (
                          <tr key={i}>

                            <td style={tdStyle}>
                              <div style={{ minWidth: "180px" }}>
                                <Select
                                  styles={customSelectStyles}
                                  options={uniqueProcessIds.map(p => ({ value: p.process_id, label: `${p.process_id} - ${p.process_name}` }))}
                                  value={op.process_id ? { value: op.process_id, label: `${op.process_id} - ${op.process_name}` } : null}
                                  onChange={(selected) => handleProcessIdSelect(i, selected ? selected.value : "")}
                                  isDisabled={isViewing}
                                  placeholder="Select"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                />
                              </div>
                            </td>

                            <td style={tdStyle}>
                              <input style={{ ...smallInput, background: "#f5f5f5", minWidth: "120px" }} value={op.process_name || ""} readOnly />
                            </td>

                            <td style={tdStyle}>
                              <div style={{ minWidth: "180px" }}>
                                <Select
                                  styles={customSelectStyles}
                                  options={getSubProcessOptions(op.process_id).map(s => ({ value: s.sub_process_id, label: `${s.sub_process_id} - ${s.sub_process_name}` }))}
                                  value={op.sub_process_id ? { value: op.sub_process_id, label: `${op.sub_process_id} - ${op.sub_process_name}` } : null}
                                  onChange={(selected) => handleSubProcessIdSelect(i, selected ? selected.value : "")}
                                  isDisabled={isViewing || !op.process_id}
                                  placeholder="Select"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                />
                              </div>
                            </td>

                            <td style={tdStyle}>
                              <input style={{ ...smallInput, background: "#f5f5f5", minWidth: "120px" }} value={op.sub_process_name || ""} readOnly />
                            </td>

                            <td style={tdStyle}>
                              <input style={{ ...smallInput, background: "#f5f5f5", minWidth: "140px" }} value={op.workcenterName || ""} readOnly />
                            </td>

                            <td style={tdStyle}>
                              <input style={{ ...smallInput, background: "#f5f5f5", minWidth: "140px" }} value={op.workcenter_description || ""} readOnly />
                            </td>

                            <td style={tdStyle}>
                              {!isViewing && (
                                <button onClick={() => removeOperationRow(i)} style={{ background: "transparent", border: "none", padding: "4px", color: "#ef4444", cursor: "pointer" }}>
                                  <FaTrash size={16} />
                                </button>
                              )}
                            </td>

                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* TABS MENU */}
                <div style={{ display: "flex", gap: 15, marginTop: 15, borderBottom: "2px solid #e2e8f0", flexShrink: 0, overflowX: "auto" }}>
                  {[
                    { id: "components", label: "Material Details" },
                    { id: "process_params", label: "Process Parameter" },
                    { id: "machine_params", label: "Machine Parameter" },
                    { id: "activities", label: "Machine Time" },
                    { id: "inspection_params", label: "Inspection Parameter" },
                    { id: "docs", label: "Docs" },
                    { id: "changeLog", label: "Change Log" }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        padding: "8px 12px",
                        border: "none",
                        cursor: "pointer",
                        background: "transparent",
                        borderBottom: activeTab === tab.id ? "3px solid #1976d2" : "3px solid transparent",
                        fontWeight: activeTab === tab.id ? 700 : 500,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* TABS CONTENT */}

                {activeTab === "activities" && (() => {
                  const hasValue = (val) => val !== undefined && val !== null && val !== "";
                  const showSetup = operations.some((op) => { const wc = getWc(op.workcenterName); return (Number(wc.setup) === 1) || isDraftView || hasValue(op.activities?.setup_value); });
                  const showMachine = operations.some((op) => { const wc = getWc(op.workcenterName); return (Number(wc.machine) === 1) || isDraftView || hasValue(op.activities?.machine_value); });
                  const showLabour = operations.some((op) => { const wc = getWc(op.workcenterName); return (Number(wc.labour) === 1) || isDraftView || hasValue(op.activities?.labour_value); });
                  const showOverhead = operations.some((op) => { const wc = getWc(op.workcenterName); return (Number(wc.overhead) === 1) || isDraftView || hasValue(op.activities?.overhead_value); });

                  const hasSubprocess = operations.some(op => op.sub_process_id || op.sub_process_name);
                  const showSharedUom = showSetup || showMachine || showLabour;

                  const fixedTh = (w) => ({ ...thStyle, width: w, minWidth: w, maxWidth: w, overflow: "hidden", textOverflow: "ellipsis" });
                  const fixedTd = (w) => ({ ...tdStyle, width: w, minWidth: w, maxWidth: w, overflow: "hidden", textOverflow: "ellipsis" });

                  return (
                    <div style={{ overflowX: "auto", marginTop: 8 }}>
                      <table style={{ borderCollapse: "collapse", border: "1px solid #e2e8f0", tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc" }}>
                            <th style={fixedTh(150)}>Process</th>
                            {hasSubprocess && <th style={fixedTh(150)}>Subprocess</th>}
                            <th style={fixedTh(180)}>Workcenter</th>
                            {showSetup && <th style={fixedTh(110)}>Setup</th>}
                            {showMachine && <th style={fixedTh(110)}>Run Time</th>}
                            {showLabour && <th style={fixedTh(110)}>Labour</th>}
                            {showSharedUom && <th style={fixedTh(80)}>UOM</th>}
                            {showOverhead && <th style={fixedTh(110)}>Overhead</th>}
                            {showOverhead && <th style={fixedTh(100)}>Overhead UOM</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {operations.map((op, i) => {
                            const wc = getWc(op.workcenterName);
                            const inp = { ...smallInput, width: "100%" };
                            return (
                              <tr key={i}>
                                <td style={fixedTd(150)}>{op.process_name || op.process_id || ""}</td>
                                {hasSubprocess && <td style={fixedTd(150)}>{op.sub_process_name || op.sub_process_id || ""}</td>}
                                <td style={fixedTd(180)}>{op.workcenterName || ""}</td>

                                {showSetup && (
                                  <td style={fixedTd(110)}>
                                    <input type="number" style={inp} value={op.activities?.setup_value || ""} disabled={!wc.setup} onChange={(e) => updateActivityValue(i, "setup_value", e.target.value)} readOnly={isViewing} />
                                  </td>
                                )}
                                {showMachine && (
                                  <td style={fixedTd(110)}>
                                    <input type="number" style={inp} value={op.activities?.machine_value || ""} disabled={!wc.machine} onChange={(e) => updateActivityValue(i, "machine_value", e.target.value)} readOnly={isViewing} />
                                  </td>
                                )}
                                {showLabour && (
                                  <td style={fixedTd(110)}>
                                    <input type="number" style={inp} value={op.activities?.labour_value || ""} disabled={!wc.labour} onChange={(e) => updateActivityValue(i, "labour_value", e.target.value)} readOnly={isViewing} />
                                  </td>
                                )}

                                {showSharedUom && (
                                  <td style={fixedTd(80)}>
                                    {wc.setup_uom || wc.machine_uom || wc.labour_uom || op.activities?.setup_uom || op.activities?.machine_uom || op.activities?.labour_uom || "-"}
                                  </td>
                                )}

                                {showOverhead && (
                                  <td style={fixedTd(110)}>
                                    <input type="number" style={inp} value={op.activities?.overhead_value || ""} disabled={!wc.overhead} onChange={(e) => updateActivityValue(i, "overhead_value", e.target.value)} readOnly={isViewing} />
                                  </td>
                                )}
                                {showOverhead && (
                                  <td style={fixedTd(100)}>
                                    {wc.overhead_uom || op.activities?.overhead_uom || "-"}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {activeTab === "process_params" && (
                  <div style={{ marginTop: 8, overflowX: "auto" }}>
                    {operations.every(op => (op.process_parameters || []).length === 0) ? (
                      <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>
                        No process parameters found.
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc" }}>
                            <th style={thStyle}>Process</th>
                            {operations.some(o => o.sub_process_id || o.sub_process_name) && <th style={thStyle}>Subprocess</th>}
                            <th style={thStyle}>Parameter</th>
                            <th style={thStyle}>UOM</th>
                            <th style={thStyle}>Specified Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operations.flatMap((op, i) =>
                            (op.process_parameters || []).map((param, index) => (
                              <tr key={`${i}-${index}`} style={{ background: index % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                <td style={tdStyle}>{op.process_name || op.process_id || ""}</td>
                                {operations.some(o => o.sub_process_id || o.sub_process_name) && <td style={tdStyle}>{op.sub_process_name || op.sub_process_id || ""}</td>}
                                <td style={{ ...tdStyle, fontWeight: 500, color: "#1e293b" }}>{param.parameter_name || ""}</td>
                                <td style={tdStyle}>
                                  <input
                                    style={{ ...smallInput, width: 80 }}
                                    value={param.parameter_uom || ""}
                                    readOnly={isViewing}
                                    onChange={(e) => {
                                      setOperations((prev) => {
                                        const updated = [...prev];
                                        const params = [...(updated[i].process_parameters || [])];
                                        params[index] = { ...params[index], parameter_uom: e.target.value };
                                        updated[i].process_parameters = params;
                                        return updated;
                                      });
                                    }}
                                    placeholder="UOM"
                                  />
                                </td>

                                <td style={tdStyle}>
                                  <input
                                    style={{ ...smallInput, width: 140 }}
                                    value={param.parameter_value || ""}
                                    readOnly={isViewing}
                                    onChange={(e) => {
                                      setOperations((prev) => {
                                        const updated = [...prev];
                                        const params = [...(updated[i].process_parameters || [])];
                                        params[index] = { ...params[index], parameter_value: e.target.value };
                                        updated[i].process_parameters = params;
                                        return updated;
                                      });
                                    }}
                                  />
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {activeTab === "machine_params" && (
                  <div style={{ marginTop: 8, overflowX: "auto" }}>
                    {operations.every(op => (op.machine_parameters || []).length === 0) ? (
                      <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>
                        No machine parameters found.
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc" }}>
                            <th style={thStyle}>Process</th>
                            {operations.some(o => o.sub_process_id || o.sub_process_name) && <th style={thStyle}>Subprocess</th>}
                            <th style={thStyle}>Workcenter</th>
                            <th style={thStyle}>Parameter</th>
                            <th style={thStyle}>UOM</th>
                            <th style={thStyle}>Specified Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operations.flatMap((op, i) =>
                            (op.machine_parameters || []).map((p, idx) => (
                              <tr key={`${i}-${idx}`} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                <td style={tdStyle}>{op.process_name || op.process_id || ""}</td>
                                {operations.some(o => o.sub_process_id || o.sub_process_name) && <td style={tdStyle}>{op.sub_process_name || op.sub_process_id || ""}</td>}
                                <td style={tdStyle}>{op.workcenterName || ""}</td>
                                <td style={{ ...tdStyle, fontWeight: 500, color: "#1e293b" }}>{p.name || ""}</td>
                                <td style={{ ...tdStyle, color: "#64748b" }}>{p.uom || ""}</td>
                                <td style={tdStyle}>
                                  <input
                                    style={{ ...smallInput, width: 120 }}
                                    value={p.value || ""}
                                    readOnly={isViewing}
                                    onChange={(e) => updateParameterValue(i, "machine_parameters", idx, e.target.value)}
                                  />
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {activeTab === "components" && (
                  <div>
                    {!isViewing && !componentsReadOnly && <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10, marginBottom: 10 }}><button onClick={addComponentRow} style={{ padding: "6px 10px", borderRadius: 6, border: "none", cursor: "pointer", background: "#1976d2", color: "white" }}>+ Add Component</button></div>}
                    <div style={{ width: "100%", overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1100 }}>
                        <thead>
                          <tr style={{ background: "#f8fafc" }}>
                            <th style={thStyle}>Item</th>
                            <th style={thStyle}>Process</th>
                            {operations.some(o => o.sub_process_id || o.sub_process_name) && <th style={thStyle}>Sub Process</th>}
                            <th style={thStyle}>Product Type</th>
                            <th style={thStyle}>Product code</th>
                            <th style={thStyle}>Description</th>
                            <th style={thStyle}>UOM</th>
                            <th style={thStyle}>Qty</th>
                            <th style={thStyle}>Assembly</th>
                            <th style={thStyle}>Valid From</th>
                            <th style={thStyle}>Valid To</th>
                            <th style={thStyle}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {components.map((c, idx) => (
                            <tr key={idx}>
                              <td style={tdStyle}><input style={{ ...smallInput, width: 50 }} value={c.item} onChange={(e) => updateComponentField(idx, "item", e.target.value)} readOnly={isViewing || componentsReadOnly} /></td>
                              <td style={tdStyle}>
                                <div style={{ minWidth: "160px" }}>
                                  <Select
                                    styles={customSelectStyles}
                                    options={
                                      Array.from(
                                        new Map(
                                          operations
                                            .filter(op => op.process_id)
                                            .map(op => [op.process_id, { value: op.process_id, label: `${op.process_id} - ${op.process_name || ""}` }])
                                        ).values()
                                      )
                                    }
                                    value={c.process_id ? { value: c.process_id, label: `${c.process_id} - ${operations.find(op => op.process_id === c.process_id)?.process_name || ""}` } : null}
                                    onChange={(selected) => {
                                      updateComponentField(idx, "process_id", selected ? selected.value : "");
                                      updateComponentField(idx, "sub_process_id", "");
                                    }}
                                    isDisabled={isViewing}
                                    placeholder="Select"
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                  />
                                </div>
                              </td>
                              {operations.some(o => o.sub_process_id || o.sub_process_name) && (
                                <td style={tdStyle}>
                                  <div style={{ minWidth: "160px" }}>
                                    <Select
                                      styles={customSelectStyles}
                                      options={
                                        Array.from(
                                          new Map(
                                            operations
                                              .filter(op => op.process_id === c.process_id && op.sub_process_id)
                                              .map(op => [op.sub_process_id, { value: op.sub_process_id, label: `${op.sub_process_id} - ${op.sub_process_name || ""}` }])
                                          ).values()
                                        )
                                      }
                                      value={c.sub_process_id ? { value: c.sub_process_id, label: `${c.sub_process_id} - ${operations.find(op => op.sub_process_id === c.sub_process_id)?.sub_process_name || ""}` } : null}
                                      onChange={(selected) => updateComponentField(idx, "sub_process_id", selected ? selected.value : "")}
                                      isDisabled={isViewing || !c.process_id}
                                      placeholder="Select"
                                      isClearable
                                      menuPortalTarget={document.body}
                                      menuPosition="fixed"
                                    />
                                  </div>
                                </td>
                              )}
                              <td style={tdStyle}><input style={{ ...smallInput, width: 100 }} value={c.product_type || ""} onChange={(e) => updateComponentField(idx, "product_type", e.target.value)} readOnly={isViewing || componentsReadOnly} /></td>
                              <td style={tdStyle}>
                                <div style={{ minWidth: "180px" }}>
                                  <Select
                                    styles={customSelectStyles}
                                    options={products.map(p => {
                                      const code = p.productCode || p.productcode;
                                      const desc = p.productDescription || "";
                                      return {
                                        value: code,
                                        label: desc ? `${code} - ${desc}` : code
                                      };
                                    })}
                                    value={
                                      c.component_product
                                        ? {
                                          value: c.component_product,
                                          label: (() => {
                                            const p = products.find(x => String(x.productCode || x.productcode) === String(c.component_product));
                                            const desc = p?.productDescription || "";
                                            return desc ? `${c.component_product} - ${desc}` : c.component_product;
                                          })()
                                        }
                                        : null
                                    }
                                    onChange={(selected) => {
                                      const val = selected ? selected.value : "";
                                      const prod = products.find((p) => String(p.productCode || p.productcode) === String(val));
                                      updateComponentField(idx, "component_product", val);
                                      updateComponentField(idx, "description", prod?.productDescription || "");
                                      updateComponentField(idx, "uom", prod?.uom || "");
                                    }}
                                    isDisabled={isViewing || componentsReadOnly}
                                    placeholder="Select Code"
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                  />
                                </div>
                              </td>
                              <td style={tdStyle}><input style={{ ...smallInput, background: "#f5f5f5", width: 120 }} value={c.description} readOnly /></td>
                              <td style={tdStyle}><input style={{ ...smallInput, background: "#f5f5f5", width: 50 }} value={c.uom || ""} readOnly /></td>
                              <td style={tdStyle}><input type="number" style={{ ...smallInput, width: 60 }} value={c.quantity ?? ""} onChange={(e) => updateComponentField(idx, "quantity", e.target.value)} readOnly={isViewing || componentsReadOnly} /></td>

                              <td style={tdStyle}>
                                <div style={{ minWidth: "90px" }}>
                                  <Select
                                    styles={customSelectStyles}
                                    options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                    value={c.assembly ? { value: c.assembly, label: c.assembly } : null}
                                    onChange={(selected) => updateComponentField(idx, "assembly", selected ? selected.value : "")}
                                    isDisabled={isViewing || componentsReadOnly}
                                    placeholder="Select"
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                  />
                                </div>
                              </td>
                              <td style={tdStyle}><input type="date" style={{ ...smallInput, width: 110 }} value={c.valid_from} onChange={(e) => updateComponentField(idx, "valid_from", e.target.value)} readOnly={isViewing || componentsReadOnly} /></td>
                              <td style={tdStyle}><input type="date" style={{ ...smallInput, width: 110 }} value={c.valid_to} onChange={(e) => updateComponentField(idx, "valid_to", e.target.value)} readOnly={isViewing || componentsReadOnly} /></td>
                              <td style={{ ...tdStyle, textAlign: "center" }}>
                                {!isViewing && !componentsReadOnly && <button onClick={() => removeComponent(idx)} style={{ background: "transparent", border: "none", padding: "4px", color: "#ef4444", cursor: "pointer" }}><FaTrash size={16} /></button>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 5. DOCUMENTS TAB */}
                {activeTab === "docs" && (
                  <div>
                    {!isViewing && <input type="file" multiple onChange={(e) => setDocuments([...e.target.files])} style={{ padding: "6px", border: "1px solid #ccc", borderRadius: "4px" }} />}
                    {isViewing && <p style={{ color: "#666" }}>Documents are view-only.</p>}
                  </div>
                )}

                {/* 6. INSPECTION PARAMS TAB */}
                {activeTab === "inspection_params" && (
                  <div style={{ marginTop: 8, overflowX: "auto" }}>
                    {operations.every(op => (op.inspection_parameters || []).length === 0) ? (
                      <div style={{ padding: 30, textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 8, border: "1px dashed #cbd5e1" }}>
                        <h4 style={{ margin: 0 }}>Inspection Parameters</h4>
                        <p style={{ margin: "5px 0 0 0", fontSize: 13 }}>
                          No inspection characteristics found. Select a process/subprocess in Operations to load them.
                        </p>
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0", fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: "#f8fafc" }}>
                            <th style={thStyle}>Process</th>
                            {operations.some(o => o.sub_process_id || o.sub_process_name) && <th style={thStyle}>Subprocess</th>}
                            <th style={thStyle}>Parameter</th>
                            <th style={thStyle}>UOM</th>
                            <th style={thStyle}>Specified Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {operations.flatMap((op, opIdx) =>
                            (op.inspection_parameters || []).map((ip, ipIdx) => (
                              <tr
                                key={`${opIdx}-${ipIdx}`}
                                style={{ background: ipIdx % 2 === 0 ? "#fff" : "#fafbfc" }}
                              >
                                <td style={tdStyle}>{op.process_name || op.process_id || ""}</td>
                                {operations.some(o => o.sub_process_id || o.sub_process_name) && <td style={tdStyle}>{op.sub_process_name || op.sub_process_id || ""}</td>}
                                <td style={{ ...tdStyle, fontWeight: 500, color: "#1e293b" }}>{ip.characteristic || ""}</td>
                                <td style={{ ...tdStyle, color: "#64748b" }}>{ip.uom || ""}</td>
                                <td style={tdStyle}>
                                  <input
                                    style={{ ...smallInput, width: 140 }}
                                    value={ip.parameter_value || ""}
                                    readOnly={isViewing}
                                    placeholder="Enter value"
                                    onChange={(e) => {
                                      setOperations((prev) => {
                                        const updated = [...prev];
                                        const params = [...(updated[opIdx].inspection_parameters || [])];
                                        params[ipIdx] = { ...params[ipIdx], parameter_value: e.target.value };
                                        updated[opIdx] = { ...updated[opIdx], inspection_parameters: params };
                                        return updated;
                                      });
                                    }}
                                  />
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* 7. CHANGE LOG TAB */}
                {activeTab === "changeLog" && (
                  <div style={{ background: "#fff", borderRadius: 10, padding: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc" }}>
                            <th style={thStyle}>Date & Time</th>
                            <th style={thStyle}>User</th>
                            <th style={thStyle}>Action</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Field</th>
                            <th style={thStyle}>Old Value</th>
                            <th style={thStyle}>New Value</th>
                            <th style={thStyle}>Comment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {routingLogs.length > 0 ? (
                            routingLogs.map((log, index) => (
                              <tr key={log.id || index}>
                                <td style={tdStyle}>{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</td>
                                <td style={tdStyle}>{log.created_by || "Admin"}</td>
                                <td style={tdStyle}>{log.action_type || "-"}</td>
                                <td style={tdStyle}>{log.doc_status || "-"}</td>
                                <td style={tdStyle}>{log.field_name || "-"}</td>
                                <td style={tdStyle}>{log.old_value || "-"}</td>
                                <td style={{ ...tdStyle, fontWeight: 700, color: "#0f172a" }}>{log.new_value || "-"}</td>
                                <td style={tdStyle}>{log.comment || "-"}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" style={{ textAlign: "center", padding: 20, color: "#64748b" }}>No change logs available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              </div>

              {/* SFG PICKER */}
              {sfgChoices.length > 0 && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2000
                  }}
                >
                  <div style={{ background: "white", borderRadius: 8, padding: 20, minWidth: 320, maxWidth: 440 }}>
                    <h4 style={{ marginTop: 0 }}>Multiple Semi-Finished Components Found</h4>
                    <p style={{ fontSize: 13, color: "#555" }}>
                      This product has more than one Semi-Finished (SFG) component. Select which one to route next:
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10, maxHeight: 280, overflowY: "auto" }}>
                      {sfgChoices.map((c) => (
                        <button
                          key={c.component_product}
                          onClick={() => goToSfg(formHeader.productCode, c.component_product)}
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            border: "1px solid #ccc",
                            borderRadius: 6,
                            background: "#f8f9fa",
                            cursor: "pointer"
                          }}
                        >
                          <strong>{c.component_product}</strong>
                          {c.description ? ` — ${c.description}` : ""}
                        </button>
                      ))}
                    </div>
                    <div style={{ marginTop: 14, textAlign: "right" }}>
                      <button
                        onClick={() => setSfgChoices([])}
                        style={{ padding: "6px 14px", border: "none", background: "#e2e8f0", borderRadius: 6, cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* FOOTER */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginTop: 15,
                  paddingTop: 15,
                  borderTop: "1px solid #eee",
                  flexShrink: 0
                }}
              >
                {bomLevelStack.length > 1 && (
                  <button
                    onClick={handleBackBom}
                    style={{
                      padding: "8px 20px",
                      background: "#64748b",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontWeight: 600
                    }}
                  >
                    Back
                  </button>
                )}

                {components.filter(isSfgComponent).length > 0 && (
                  <button
                    onClick={handleNextBom}
                    style={{
                      padding: "8px 20px",
                      background: "#1976d2",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontWeight: 600
                    }}
                  >
                    Next
                  </button>
                )}

                {!isViewing && (
                  <button
                    onClick={() => {
                      if (!editingId && !canCreate) {
                        toast.error("You don't have Create access.Please contact admin.");
                        return;
                      }
                      if (editingId && !canEdit) {
                        toast.error("You don't have Edit access.Please contact admin.");
                        return;
                      }
                      handleSave();
                    }}
                    disabled={saving}
                    style={{
                      padding: "8px 20px",
                      background: "#1e293b",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontWeight: "bold"
                    }}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                )}
              </div>
            </div>
          </>,
          document.body
        )}

        {/* ===================== DRAFTS MODAL ===================== */}
        {showDraftModal && ReactDOM.createPortal(
          <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "85vw", height: "85vh", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.2)", zIndex: 10001 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 15, marginBottom: 15 }}>
                <h3 style={{ margin: 0, color: "#1e293b" }}>👀 Preview Uploaded Routings</h3>
                <button onClick={() => setShowDraftModal(false)} style={{ background: "#f1f5f9", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
              </div>

              <div style={{ flex: 1, overflowY: "auto", paddingRight: 10 }}>
                {draftRoutings.length === 0 ? (
                  <p style={{ textAlign: 'center', marginTop: 40, color: '#666' }}>No Pending Routings found.</p>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f1f1f1" }}>
                        <th style={thStyle}>Draft ID</th>
                        <th style={thStyle}>Product Code</th>
                        <th style={thStyle}>Alt BOM</th>
                        <th style={thStyle}>Location</th>
                        <th style={thStyle}>Operations</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draftRoutings.map((draft) => (
                        <tr key={draft.routing_id}>
                          <td style={tdStyle}><strong>{draft.routingCode}</strong></td>
                          <td style={tdStyle}>{draft.productCode}</td>
                          <td style={tdStyle}>{draft.alternative_bom || "-"}</td>
                          <td style={tdStyle}>{draft.mainLocation || "-"}</td>
                          <td style={tdStyle}>{draft.op_count}</td>
                          <td style={tdStyle}>
                            <button
                              onClick={() => {
                                if (!canView) {
                                  toast.error("You don't have View access.Please contact admin.");
                                  return;
                                }
                                openFormModal(draft.routing_id, 'draft');
                              }}
                              style={{ padding: "6px 10px", background: "#1E88E5", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}
                            >
                              View
                            </button>
                            <button
                              onClick={() => {
                                if (!canApprove) {
                                  toast.error("You don't have Approve access.Please contact admin.");
                                  return;
                                }
                                handlePublishDraft(draft.routing_id);
                              }}
                              disabled={publishingId === draft.routing_id || isPublishingAll}
                              style={{ padding: "6px 10px", background: (publishingId === draft.routing_id || isPublishingAll) ? "#6c757d" : "#10b981", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}
                            >
                              {publishingId === draft.routing_id ? "Saving..." : "Save"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {draftRoutings.length > 0 && (
                <div style={{ borderTop: "1px solid #eee", paddingTop: 15, marginTop: 15, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={() => {
                      if (!canApprove) {
                        toast.error("You don't have Approve access.Please contact admin.");
                        return;
                      }
                      handlePublishAllDrafts();
                    }}
                    disabled={isPublishingAll}
                    style={{
                      background: isPublishingAll ? "#6c757d" : "#10b981",
                      color: "white",
                      border: "none",
                      padding: "10px 24px",
                      borderRadius: 6,
                      cursor: isPublishingAll ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: 15
                    }}
                  >
                    {isPublishingAll ? "Saving All..." : "Approve All"}
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

        {/* ===================== ORPHAN ROUTINGS MODAL ===================== */}
        {/* Routings that exist in routings_data but whose productCode has no
            matching row in productdetails — these are what's "missing" from
            the Finished/Semi-Finished counts, since those counts INNER JOIN
            productdetails and silently drop anything without a match. */}
        {showOrphanModal && ReactDOM.createPortal(
          <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "50vw", maxWidth: 700, maxHeight: "75vh", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.2)", zIndex: 10001 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 15, marginBottom: 15 }}>
                <h3 style={{ margin: 0, color: "#1e293b" }}>⚠ Routings Missing From Product Master</h3>
                <button onClick={() => setShowOrphanModal(false)} style={{ background: "#f1f5f9", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
              </div>

              <div style={{ flex: 1, overflowY: "auto", paddingRight: 10 }}>
                {orphanRoutings.length === 0 ? (
                  <p style={{ textAlign: 'center', marginTop: 40, color: '#666' }}>
                    None found — every routing's product code exists in the Product Master.
                  </p>
                ) : (
                  <>
                    <p style={{ color: "#666", fontSize: 13, marginTop: 0 }}>
                      These {orphanRoutings.length} routing{orphanRoutings.length === 1 ? "" : "s"} have a
                      product code that isn't maintained in the Product Master, so they don't get a
                      Product Type and are excluded from the Finished Goods / Semi Finished counts.
                    </p>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                      <thead>
                        <tr style={{ background: "#f1f1f1" }}>
                          <th style={thStyle}>Routing Code</th>
                          <th style={thStyle}>Product Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orphanRoutings.map((o) => (
                          <tr key={o.routing_id}>
                            <td style={tdStyle}><strong>{o.routingCode}</strong></td>
                            <td style={tdStyle}>{o.productCode || <em style={{ color: "#999" }}>(blank)</em>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* ===================== UPLOAD MODAL ===================== */}
        {showUploadModal && ReactDOM.createPortal(
          <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "400px", borderRadius: 12, padding: 25, boxShadow: "0 10px 30px rgba(0,0,0,0.2)", zIndex: 10001 }}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h4 style={{ margin: 0, fontSize: "18px", color: "#1e293b" }}>Upload Routings</h4>
                <button onClick={() => setShowUploadModal(false)} style={{ background: "transparent", border: "none", fontSize: 18, cursor: "pointer", color: "#64748b" }}>✖</button>
              </div>

              <div style={{ border: "2px dashed #cbd5e1", borderRadius: 8, padding: 30, textAlign: "center", background: "#f8fafc" }}>
                <p style={{ margin: "0 0 15px 0", fontSize: 14, color: "#475569" }}>Upload your Routings Template</p>

                <input
                  type="file"
                  id="routingExcelUploadReal"
                  accept=".xlsx, .xls, .csv"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    setShowUploadModal(false);
                    handleExcelUpload(e);
                  }}
                />

                <label htmlFor="routingExcelUploadReal" style={{ background: "#021843", color: "white", padding: "8px 20px", borderRadius: 6, cursor: "pointer", fontWeight: 600, display: "inline-block", marginBottom: 15 }}>
                  Browse File
                </label>

                <div
                  style={{
                    marginTop: 15,
                    display: "flex",
                    justifyContent: "center"
                  }}
                >
                  <a
                    href="/assets/files/routing.xlsx"
                    download
                    style={{
                      fontSize: 13,
                      color: "#021843",
                      textDecoration: "none",
                      fontWeight: 500
                    }}
                  >
                    ⬇ Download Template
                  </a>
                </div>
              </div>

            </div>
          </div>,
          document.body
        )}

        {/* ===================== ROUTING TREE MODAL ===================== */}
        {showTreeModal && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1200 }}>
            <div style={{ width: "70vw", height: "75vh", padding: 14, margin: "50px auto", background: "#f8fafc", borderRadius: 12, overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Routing Tree</h3>
                  <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "4px 12px", borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                    Product: {routingTree?.code}
                  </span>
                </div>
                <button
                  onClick={() => setShowTreeModal(false)}
                  style={{ background: "#ef4444", color: "white", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                >
                  Close
                </button>
              </div>

              {routingTree && renderRoutingNode(routingTree)}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Routings;